import { useToast } from '@app/core/notifications';
import { Device, DeviceVariable, Service } from '@mi-front/core';
import { FormModal, ModalController, NullState, TextInput } from '@mi-front/ui';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { apiClient } from '../../../../../../api';

interface PropsP {
  readonly service: Service;
  readonly load: () => Promise<{ vars: readonly DeviceVariable[] }>;
  readonly save: (data: { vars: readonly DeviceVariable[] }) => Promise<void>;
}

export function ServiceConfigP(p: PropsP) {
  const { service, load, save } = p;
  const { $t } = useIntl();
  const { notifyInfo, notifyError } = useToast();

  return (
    <ModalController
      trigger={(open) => (
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          sx={{ marginLeft: 1.5 }}
          onClick={open}
          startIcon={<SettingsIcon />}
        >
          {$t({
            id: 'streamerBox.viewScreen.services.actions.config.btn',
            defaultMessage: 'Config',
          })}
        </Button>
      )}
    >
      {({ show, onHide }) => (
        <FormModal
          show={show}
          onHide={onHide}
          initialValues={() => load()}
          onSubmit={async (data) => {
            try {
              await save(data);
              setImmediate(onHide);
              notifyInfo(
                $t({
                  id: 'streamerBox.viewScreen.services.config.done',
                  defaultMessage: 'Configuración de servicio actualizada',
                }),
              );
            } catch (err: any) {
              notifyError(
                $t({
                  id: 'streamerBox.viewScreen.services.config.error',
                  defaultMessage: 'No se pudo actualizar la configuración del servicio',
                }),
              );
            }
          }}
          title={
            <>
              <SettingsIcon />{' '}
              <FormattedMessage
                id="streamerBox.viewScreen.services.actions.config.title"
                defaultMessage="Configurar servicio"
              />{' '}
              <strong>{service.baseService.name.toUpperCase()}</strong>
            </>
          }
        >
          {(fp) => {
            if (!fp.values.vars?.length) {
              return (
                <NullState className="py-5">
                  <FormattedMessage
                    id="streamerBox.viewScreen.services.config.noVars"
                    defaultMessage="No hay variables a configurar para este servicio"
                  />
                </NullState>
              );
            }

            return fp.values.vars.map((_var, index) => {
              return (
                <TextInput
                  key={index}
                  name={`vars.${index}.value`}
                  label={_var.name.replace(/_/g, ' ')}
                  value={_var.value}
                  onChange={fp.handleChange}
                  onBlur={fp.handleBlur}
                />
              );
            });
          }}
        </FormModal>
      )}
    </ModalController>
  );
}

interface Props {
  readonly device: Device;
  readonly service: Service;
}

export function ServiceConfig(p: Props) {
  const { device, service } = p;

  return (
    <ServiceConfigP
      service={service}
      load={async () => {
        const data = await apiClient.streamerBox.fetchVariables(device.id);

        return {
          vars: data.filter((v) => v.serviceId === service.baseService.id),
        };
      }}
      save={async (data: { vars: readonly DeviceVariable[] }) => {
        await apiClient.streamerBox.updateVariables(device.id, data.vars);
      }}
    />
  );
}
